import React, { useState, useEffect } from "react";
import AddressAutocomplete from "./AddressAutocomplete";
import { UMSSBlueDark } from "../utils/colors";

// Verification Modal Component
const VerificationModal = ({ onClose }) => {
  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "1.5rem",
        width: "90%",
        maxWidth: "600px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
      }}>
        <h2 style={{ marginTop: 0 }}>Information Pre-filled</h2>
        
        <p>
          We've automatically filled in your demographic information based on your previous record.
          Please verify that all information is up-to-date and make any necessary changes.
        </p>
        
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
          <button
            onClick={onClose}
            style={{
              padding: "0.7rem 1.5rem",
              borderRadius: "8px",
              border: "none",
              backgroundColor: UMSSBlueDark,
              color: "#fff",
              cursor: "pointer"
            }}
          >
            Verify & Continue
          </button>
        </div>
      </div>
    </div>
  );
};

function Demographics({ demographics, onChange, showVerificationModal, onVerificationModalClose }) {
  const [showModal, setShowModal] = useState(false);

  // Show verification modal when component mounts if data is pre-filled
  useEffect(() => {
    if (showVerificationModal) {
      setShowModal(true);
    }
  }, [showVerificationModal]);

  // Close the verification modal
  const handleCloseModal = () => {
    setShowModal(false);
    if (onVerificationModalClose) {
      onVerificationModalClose();
    }
  };

  // Helper arrays for each category
  const genderOptions = ["Male", "Female"];
  const raceOptions = [
    "White",
    "Black / African American",
    "Asian",
    "American Indian",
  ];
  const maritalOptions = ["Single", "Married", "Divorced", "Widowed"];
  const ethnicityOptions = ["Hispanic/Latino", "Not Hispanic/Latino"];
  const incomeOptions = [
    "1 Person - $2430 or Less",
    "2 Persons - $3287 or Less",
    "3 Persons - $4143 or Less",
    "4 Persons - $5000 or Less",
    "Zero - No Income",
  ];

  // Check if the given value is selected for a particular key
  const isSelected = (key, value) => demographics[key] === value;

  // Single selection for each category (e.g., "gender", "race", etc.)
  const handleSelect = (key, value) => {
    const updates = { ...demographics, [key]: value };
    
    // If we're updating income, also extract and set familySize
    if (key === 'income') {
      let familySize = "0";  // Initialize as string "0"
      
      if (value.startsWith('Zero')) {
        familySize = "0";  // String "0"
      } else {
        // Extract the number from strings like "1 Person" or "2 Persons"
        const match = value.match(/^(\d+)/);
        if (match && match[1]) {
          familySize = match[1];  // Use the matched string directly instead of parsing to int
        }
      }
      
      updates.familySize = familySize;  // Set as string
      console.log(`Set family size to ${familySize} (string) based on income: "${value}"`);
    }
    
    onChange(updates);
  };

  // Handle address from Google (parsed) or manual entry; second arg is always from AddressAutocomplete
  const handleAddressChange = (formattedAddress, details = {}) => {
    const rawAddress = (details.rawAddress || formattedAddress || "").trim();
    const street = (details.street || "").trim();
    const city = (details.city || "").trim();
    const state = (details.state || "").trim();
    const zip = (details.zip || "").trim();

    const cityState =
      city && state ? `${city}, ${state}` : city || state;
    const cityStateZip =
      city && state && zip
        ? `${city}, ${state} ${zip}`
        : cityState && zip
          ? `${cityState} ${zip}`
          : cityState;

    onChange({
      ...demographics,
      rawAddress,
      address: street,
      city,
      state,
      zip,
      cityState,
      cityStateZip,
    });
  };

  // Styles for container and pills
  const containerStyle = {
    fontFamily: "Quicksand, sans-serif",
    marginBottom: "5rem",
  };

  const sectionStyle = {
    padding: "1rem",
    border: "1px solid #ccc",
    borderRadius: "8px",
    backgroundColor: "#fafafa",
    marginBottom: "1rem",
  };

  const pillRowStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "0.5rem",
  };

  const pillStyle = (selected) => ({
    padding: "0.6rem 1rem",
    borderRadius: "15px",
    fontFamily: "Quicksand, sans-serif",
    border: selected ? `2px solid ${UMSSBlueDark}` : "1px solid #ccc",
    backgroundColor: selected ? "#e6f0ff" : "#ffffff",
    color: "#333333",
    cursor: "pointer",
    fontWeight: selected ? "600" : "normal",
    colorScheme: "light only",
  });

  return (
    <div style={containerStyle}>
      {/* Show verification modal if needed */}
      {showModal && <VerificationModal onClose={handleCloseModal} />}
      
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        <h2 style={{ margin: 0 }}>Demographic Information</h2>
        <p style={{ margin: 0, color: "#777" }}>Please provide your demographic details</p>
      </div>

      {/* Show a reminder banner if demographics were pre-filled */}
      {showVerificationModal && (
        <div style={{
          padding: "0.75rem 1rem",
          backgroundColor: "#e7f3ff",
          border: "1px solid #b3d7ff",
          borderRadius: "8px",
          marginBottom: "1.5rem",
          color: "#004085"
        }}>
          <p style={{ margin: 0 }}>
            <strong>Information Pre-filled:</strong> Please verify that all information is correct and make any necessary updates.
          </p>
        </div>
      )}

      {/* Address */}
      <div style={sectionStyle}>
        <h4 style={{ margin: "0 0 0.5rem", color: "#666" }}>Address</h4>
        <AddressAutocomplete
          address={demographics.rawAddress || ""}
          street={demographics.address || ""}
          city={demographics.city || ""}
          state={demographics.state || ""}
          zip={demographics.zip || ""}
          onChange={handleAddressChange}
        />
      </div>

      {/* Gender */}
      <div style={sectionStyle}>
        <h4 style={{ margin: "0 0 0.5rem", color: "#666" }}>Gender</h4>
        <div style={pillRowStyle}>
          {genderOptions.map((option) => (
            <button
              key={option}
              type="button"
              style={pillStyle(isSelected("gender", option))}
              onClick={() => handleSelect("gender", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Race */}
      <div style={sectionStyle}>
        <h4 style={{ margin: "0 0 0.5rem", color: "#666" }}>Race</h4>
        <div style={pillRowStyle}>
          {raceOptions.map((option) => (
            <button
              key={option}
              type="button"
              style={pillStyle(isSelected("race", option))}
              onClick={() => handleSelect("race", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Marital Status */}
      <div style={sectionStyle}>
        <h4 style={{ margin: "0 0 0.5rem", color: "#666" }}>Marital Status</h4>
        <div style={pillRowStyle}>
          {maritalOptions.map((option) => (
            <button
              key={option}
              type="button"
              style={pillStyle(isSelected("maritalStatus", option))}
              onClick={() => handleSelect("maritalStatus", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Ethnicity */}
      <div style={sectionStyle}>
        <h4 style={{ margin: "0 0 0.5rem", color: "#666" }}>Ethnicity</h4>
        <div style={pillRowStyle}>
          {ethnicityOptions.map((option) => (
            <button
              key={option}
              type="button"
              style={pillStyle(isSelected("ethnicity", option))}
              onClick={() => handleSelect("ethnicity", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Income */}
      <div style={sectionStyle}>
        <h4 style={{ margin: "0 0 0.5rem", color: "#666" }}>Income</h4>
        <div style={pillRowStyle}>
          {incomeOptions.map((option) => (
            <button
              key={option}
              type="button"
              style={pillStyle(isSelected("income", option))}
              onClick={() => handleSelect("income", option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}

export default Demographics;